import { type FC, useMemo, useRef } from 'react'

import { useTranslation } from 'react-i18next'
import type { Swiper as SwiperType } from 'swiper'
import { A11y, Keyboard, Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import {
  IconPremiumPostLockedOutline,
  IconStatsClose
} from 'assets/svg/sprite'
import { type IStatsData } from 'types/interfaces/ITypes'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import './_statsPopup.scss'

interface VideoStat {
  id: string | number
  videoId?: string | number
  index: number
  total: number
  completionRate: number
  playsRaw: number
  playsUnique: number
  duration: number
  title: string
  thumb?: string | null
  isPreview: boolean
  locked: boolean
}

interface Props {
  statsData?: IStatsData
  videoStats?: VideoStat[]
  closeStats: () => void
  storyId?: string
  centered?: boolean
}

//  money-only formatter
const formatMoney = (value: unknown): string => {
  const num = Number(value)
  if (!Number.isFinite(num)) return '0.00'
  return num.toFixed(2)
}


const formatDuration = (value: unknown): string => {
  const totalSeconds = Math.max(0, Math.round(Number(value) || 0))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`
  }

  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

// Track drag to differentiate vertical vs horizontal at the overlay level
const useOverlayGuards = () => {
  const dragStart = useRef<{ x: number; y: number } | null>(null)

  const onPointerDownCap = (e: React.PointerEvent) => {
    dragStart.current = { x: e.clientX, y: e.clientY }
  }
  const onPointerMoveCap = (e: React.PointerEvent) => {
    if (!dragStart.current) return
    const dx = e.clientX - dragStart.current.x
    const dy = e.clientY - dragStart.current.y
    if (Math.abs(dy) > Math.abs(dx)) e.stopPropagation()
  }
  const onPointerEndCap = () => {
    dragStart.current = null
  }

  const onTouchStartCap = (e: React.TouchEvent) => {
    const t = e.touches[0]
    dragStart.current = { x: t.clientX, y: t.clientY }
  }
  const onTouchMoveCap = (e: React.TouchEvent) => {
    if (!dragStart.current) return
    const t = e.touches[0]
    const dx = t.clientX - dragStart.current.x
    const dy = t.clientY - dragStart.current.y
    if (Math.abs(dy) > Math.abs(dx)) e.stopPropagation()
  }
  const onTouchEndCap = () => {
    dragStart.current = null
  }

  const onWheelCap = (e: React.WheelEvent) => {
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) e.stopPropagation()
  }

  return {
    onPointerDownCap,
    onPointerMoveCap,
    onPointerEndCap,
    onTouchStartCap,
    onTouchMoveCap,
    onTouchEndCap,
    onWheelCap,
  }
}

const StatsPopup: FC<Props> = ({ statsData, videoStats = [], closeStats, storyId, centered }) => {
  const { t } = useTranslation()
  const swiperRef = useRef<any>(null)

  const {
    onPointerDownCap,
    onPointerMoveCap,
    onPointerEndCap,
    onTouchStartCap,
    onTouchMoveCap,
    onTouchEndCap,
    onWheelCap,
  } = useOverlayGuards()

  const overallRevenue = useMemo(
    () => Number(statsData?.overallRevenue ?? 0),
    [statsData]
  )

  return (
    <div
      className='stats__overlay'
      onPointerDownCapture={onPointerDownCap}
      onPointerMoveCapture={onPointerMoveCap}
      onPointerUpCapture={onPointerEndCap}
      onPointerCancelCapture={onPointerEndCap}
      onTouchStartCapture={onTouchStartCap}
      onTouchMoveCapture={onTouchMoveCap}
      onTouchEndCapture={onTouchEndCap}
      onWheelCapture={onWheelCap}
    >
      {!centered && (
      <div
        className='stats__close'
        onClick={closeStats}
        role='button'
        aria-label={t('close') ?? 'Close'}
      >
        <IconStatsClose />
      </div>
      )}
      <Swiper
        modules={[Navigation, Pagination, Keyboard, A11y]}
        navigation
        pagination={{ clickable: true }}
        keyboard={{ enabled: true }}
        nested
        touchStartPreventDefault={false}
        watchOverflow
        className='stats__slider'
        slidesPerView={1}
        spaceBetween={0}
        ref={swiperRef}
        onSwiper={(sw: SwiperType) => {
          const toggle = () => sw.el.classList.toggle('is-single', sw.slides.length <= 1)
          toggle()
          sw.on('slidesLengthChange', toggle)
        }}
      >
        {/* === SLIDE 1: Post-level stats === */}
        <SwiperSlide className='stats__slide'>
          <div className='stats__data'>
            <div className='stats__data--top'>
              <div className='stat'>
                <div className='stat__text bold white'>{t('overallRevenue')}</div>
                <div className='stat__value blue'>
                  ${formatMoney(overallRevenue)}
                </div>
              </div>

              {statsData?.price && (
                <div className='stat'>
                  <div className='stat__text'>{t('purchases')}</div>
                  <div className='stat__value'>
                    <span>{statsData?.purchasesCount ?? 0}</span>
                    <span>${statsData?.purchasesSum ?? 0}</span>
                  </div>
                </div>
              )}

              <div className='stat'>
                <div className='stat__text'>{t('tips')}</div>
                <div className='stat__value'>
                  <span>{statsData?.tipsCount ?? 0}</span>
                  <span>${formatMoney(statsData?.tipsSum)}</span>
                </div>
              </div>

              <div className='stats--divider' />
            </div>

            <div className='stats__data--bottom'>
              {statsData?.price && (
                <div className='stat'>
                  <div className='stat__text'>{t('conversionRate')}</div>
                  <div className='stat__value blue'>
                    {statsData?.conversionRate ?? 0}%
                  </div>
                </div>
              )}

              {!storyId && (
                <div className='stat'>
                  <div className='stat__text'>{t('uniqueImpressions')}</div>
                  <div className='stat__value'>
                    {statsData?.uniqueImpressions ?? 0}
                  </div>
                </div>
              )}

              <div className='stat'>
                <div className='stat__text'>{t('views')}</div>
                <div className='stat__value'>
                  {Array.isArray(statsData?.views)
                    ? statsData?.views.length
                    : statsData?.views ?? 0}
                </div>
              </div>

              {!storyId && (
                <div className='stat'>
                  <div className='stat__text'>{t('uniqueViewers')}</div>
                  <div className='stat__value'>
                    {statsData?.uniqueViews ?? 0}
                  </div>
                </div>
              )}
            </div>
          </div>
        </SwiperSlide>

        {/* === SLIDES 2..N: one slide per video === */}
{videoStats.map((v) => (
  <SwiperSlide
    key={v.id}
    className='stats__slide stats__video-slide'
  >
    <div className='stats__video-data'>
      <div className='stats__video-header'>
        <div className='stats__video-title'>
          {v.title}
        </div>
      </div>

      {v.thumb && (
        <div className='stats__video-preview-row'>
          <div className='stats__video-preview'>
            <img src={v.thumb} alt={v.title} />

            {!v.isPreview && v.locked && (
              <div className='stats__video-preview-lock'>
                <IconPremiumPostLockedOutline />
              </div>
            )}
          </div>

          <div className='stats__video-mini-stats'>
            <div className='stats__video-mini-stat'>
              <div className='stats__video-mini-label'>
                Completion
              </div>

              <div className='stats__video-mini-value'>
                {Math.round(v.completionRate)}%
              </div>

              <div className='stats__video-mini-progress'>
                <div
                  className='stats__video-mini-progress-fill'
                  style={{
                    width: `${Math.max(
                      0,
                      Math.min(100, v.completionRate)
                    )}%`,
                  }}
                />
              </div>
            </div>

            <div className='stats__video-mini-divider' />

            <div className='stats__video-mini-stat'>
              <div className='stats__video-mini-label'>
                Duration
              </div>

              <div className='stats__video-mini-value'>
                {formatDuration(v.duration)}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className='stats__video-divider' />

      <div className='stats__video-metrics'>
        <div className='stats__video-stat'>
          <span className='stats__video-stat-label'>
            Plays Raw
          </span>

          <span className='stats__video-stat-value'>
            {v.playsRaw}
          </span>
        </div>

        <div className='stats__video-stat'>
          <span className='stats__video-stat-label'>
            Plays Unique
          </span>

          <span className='stats__video-stat-value'>
            {v.playsUnique}
          </span>
        </div>
      </div>
    </div>
  </SwiperSlide>
))}
      </Swiper>
    </div>
  )
}

export default StatsPopup
